import React, { ChangeEvent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@cashew/button';
import { order } from '@cashew/types';
import {
  BackArrow,
  Box,
  Input,
  PageWithNavigation,
  Title,
} from '../../components';
import { UserState } from '../../reducers/user';
import { RootState } from '../../reducers';
import './index.css';
import { initialOrder } from '../../helpers/initialorder';
import SelectComponent from '../../components/SelectComponent';

const { api, dateFormat, priceFormat } = require('@cashew/utils');

const ns = 'dispatch';

const shippingProviderOptions = [
  { key: 'DHL', value: 'DHL' },
  { key: 'UPS', value: 'UPS' },
  { key: 'EPG', value: 'EPG' },
];

const useGetOrderDetails = (
  orderId: string,
  setOrderDetails: any,
  orderDetails: order.OrderType,
  history: any
) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (orderDetails.id !== 0) {
      return;
    }
    api
      .get({
        endpoint: `orders/${orderId}`,
      })
      .then((resp: any) => {
        setOrderDetails(resp.data);
      })
      .catch((resp: any) =>
        api.errorHandler({
          resp,
          history,
          dispatch,
          callback: () =>
            dispatch({
              type: 'OPEN_TOASTER',
              payload: {
                toasterTitle: 'Fetch order',
                toasterMessage: 'Error fetching order',
                toasterType: 'error',
                toasterVisible: true,
                toasterDismiss: null,
              },
            }),
        })
      );
  }, [orderDetails]);
};

/**
 * Orders Page RefundPage
 */
export default () => {
  const { t } = useTranslation();
  const params = useParams<{ orderId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const [orderDetails, setOrderDetails] = useState<order.OrderType | any>(
    initialOrder
  );
  const [isOffline, setIsOffline] = useState(false);

  const [shippingProvider, setShippingProvider] = useState('');
  const [shippingReference, setShippingReference] = useState('');
  const { language }: UserState = useSelector((state: RootState) => state.user);

  useGetOrderDetails(params.orderId, setOrderDetails, orderDetails, history);
  useEffect(() => {
    if (
      orderDetails?.store?.type &&
      orderDetails?.store?.type === 'sharaf_dg'
    ) {
      setIsOffline(true);
    }
  }, [orderDetails]);
  return (
    <div className={ns}>
      <Helmet>
        <title>{t('dispatch')}</title>
      </Helmet>
      <PageWithNavigation>
        <Title>
          Update dispatch
          <BackArrow
            title="Back to order details"
            to={`/${language}/orders/${params.orderId}`}
          />
        </Title>
        <Box withHeader className={`${ns}__box`}>
          <div className={`${ns}__line`}>
            <p>
              <span>{t('orderDetails.orderId')}</span>
              <strong>{orderDetails.id}</strong>
            </p>
            <p>
              <span>{t('orderDetails.orderStatus')}</span>
              <strong>
                {t(
                  `status.${
                    orderDetails.shipping?.status ===
                    order.ShippingStatusEnum.DISPATCHED
                      ? orderDetails.shipping.status
                      : orderDetails.orderStatus
                  }`
                )}
              </strong>
            </p>
            <p>
              <span>
                {t(
                  isOffline
                    ? 'orderDetails.merchantOrderCode'
                    : 'orderDetails.orderReference'
                )}
              </span>
              <strong>{orderDetails.orderReference}</strong>
            </p>
            <p>
              <span>{t('orderDetails.orderDate')}</span>
              <strong>
                {orderDetails.id > 0
                  ? dateFormat({ date: orderDetails.purchaseDate })
                  : ''}
              </strong>
            </p>
            <p>
              <span>{t('orderDetails.grossOrderTotal')}</span>
              <strong>
                {priceFormat({
                  currency: orderDetails.currencyCode,
                  amount: orderDetails.totalAmount || 0,
                })}
              </strong>
            </p>
            <p>
              <span>{t('orderDetails.totalRefundAmount')}</span>
              <strong>
                {priceFormat({
                  currency: orderDetails.currencyCode,
                  amount:
                    (orderDetails.totalAmount || 0) -
                    (orderDetails.balanceAmount || 0),
                })}
              </strong>
            </p>
            <p>
              <span>{t('orderDetails.storeId')}</span>
              <strong>{orderDetails.storeId}</strong>
            </p>
            <p>
              <span>{t('orderDetails.storeName')}</span>
              <strong>{orderDetails.store?.storeName}</strong>
            </p>
            <p>
              <span>{t('orderDetails.customerName')}</span>
              <strong>
                {`${orderDetails.customer?.firstName} ${orderDetails.customer?.lastName}`}
              </strong>
            </p>
            <p>
              <span>{t('orderDetails.customerContact')}</span>
              <strong>{orderDetails.customer?.email}</strong>
            </p>

            <div>
              <Input
                name="shippingReference"
                placeholder="Shipping reference"
                value={shippingReference}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setShippingReference(e.target.value);
                }}
              />
            </div>
            <div>
              <SelectComponent
                label="Shipping provider"
                data={[
                  { key: '', value: 'Shipping provider' },
                  ...shippingProviderOptions,
                ]}
                selected={shippingProvider}
                onSelect={setShippingProvider}
              />
            </div>
          </div>
          <footer>
            <Button
              disabled={
                shippingReference === '' ||
                orderDetails.cashewCustomerId === null
              }
              onClick={() => {
                api
                  .post({
                    endpoint: `orders/${orderDetails.id}/dispatch`,
                    params: {
                      shippingProvider,
                      shippingReference,
                    },
                  })
                  .then((resp: any) => {
                    setShippingReference('');
                    dispatch({
                      type: 'OPEN_TOASTER',
                      payload: {
                        toasterTitle: 'Order dispatched',
                        toasterMessage: resp.status,
                        toasterType: 'success',
                        toasterVisible: true,
                        toasterDismiss: () => {
                          window.location.reload();
                        },
                      },
                    });
                  })
                  .catch((error: any) => {
                    dispatch({
                      type: 'OPEN_TOASTER',
                      payload: {
                        toasterTitle: 'Order dispatched',
                        toasterMessage: error?.response?.data?.message,
                        toasterType: 'error',
                        toasterVisible: true,
                        toasterDismiss: null,
                      },
                    });
                  });
              }}
            >
              {t('button.confirm')}
            </Button>
          </footer>
        </Box>
      </PageWithNavigation>
    </div>
  );
};
